import { FlagIcon, PlusIcon } from "@heroicons/react/20/solid";
import { Link, useNavigate } from "react-router";
import { describeActivity } from "@/components/collab/Discussion";
import { entityPath } from "@/components/NotificationsButton";
import { Breadcrumbs } from "@/components/page";
import { entityIcons } from "@/components/SearchDialog";
import { Button, EmptyState, PageSpinner, ProgressBar, Tag } from "@/components/ui";
import { api } from "@/lib/api";
import { useProject } from "@/lib/project";
import { keys, useAction, useEpics, useProjectActivity } from "@/lib/queries";
import { formatDay, pluralize, timeAgo } from "@/lib/utils";

function RecentActivity() {
  const { pid, userName } = useProject();
  const activity = useProjectActivity(pid);
  if (activity.isPending) return <PageSpinner />;
  if (!activity.data?.length) return <p className="text-sm text-muted">Nothing has happened yet.</p>;
  return (
    <ul className="space-y-3">
      {activity.data.map((a) => {
        const Icon = entityIcons[a.entityType] ?? FlagIcon;
        const actor = userName(a.actorId);
        return (
          <li key={a.id} className="flex gap-2 text-sm">
            <Icon className="mt-0.5 size-4 shrink-0 text-faint" />
            <div className="min-w-0">
              <Link to={entityPath(pid, a.entityType, a.entityId)} className="block truncate font-medium hover:underline" dir="auto">
                {a.title || "Deleted item"}
              </Link>
              <div className="text-muted">
                <span dir="auto">{actor}</span> {describeActivity(a, actor)}
              </div>
              <div className="text-xs text-faint">{timeAgo(a.createdAt)}</div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function ProjectOverviewPage() {
  const { pid, bundle, canEdit, propsFor } = useProject();
  const epics = useEpics(pid);
  const navigate = useNavigate();
  const createEpic = useAction(() => api.createEpic(pid, "Untitled epic"), {
    invalidate: [keys.epics(pid)],
    onSuccess: (e) => navigate(`/p/${pid}/epics/${e.id}`),
  });
  const epicProps = propsFor("epic");
  const totals = (epics.data ?? []).reduce((acc, e) => ({ tasks: acc.tasks + e.taskCount, done: acc.done + e.doneCount }), { tasks: 0, done: 0 });

  return (
    <>
      <Breadcrumbs items={[{ label: bundle.project.name }]} />
      <div className="mx-auto max-w-6xl px-6 pt-8 pb-24 md:px-12">
        <h1 className="text-[32px] leading-tight font-bold" dir="auto">
          {bundle.project.name}
        </h1>
        {bundle.project.description && (
          <p className="mt-2 max-w-3xl whitespace-pre-wrap text-muted" dir="auto">
            {bundle.project.description}
          </p>
        )}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            ["Epics", epics.data?.length ?? 0],
            ["Tasks", totals.tasks],
            ["Done", totals.done],
            ["Members", bundle.members.length],
          ].map(([label, n]) => (
            <div key={label} className="rounded-lg border border-line px-4 py-3">
              <div className="text-xs text-muted">{label}</div>
              <div className="text-2xl font-semibold tabular-nums">{n}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_300px]">
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Epics</h2>
              {canEdit && (
                <Button size="sm" icon={PlusIcon} onClick={() => createEpic.mutate()} loading={createEpic.isPending}>
                  New epic
                </Button>
              )}
            </div>
            {epics.isPending && <PageSpinner />}
            {epics.data?.length === 0 && (
              <EmptyState icon={FlagIcon} title="No epics yet" action={canEdit && <Button variant="primary" icon={PlusIcon} onClick={() => createEpic.mutate()}>Create an epic</Button>}>
                Epics describe large pieces of work. Sprints and tasks live inside them.
              </EmptyState>
            )}
            <div className="space-y-2">
              {epics.data?.map((e) => {
                const dates = epicProps.filter((p) => p.type === "date" && e.values[p.id]?.length);
                return (
                  <Link key={e.id} to={`/p/${pid}/epics/${e.id}`} className="block rounded-lg border border-line px-4 py-3 transition-colors hover:bg-hover">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <FlagIcon className="size-4 shrink-0 text-muted" />
                      <span className="min-w-0 flex-1 truncate font-medium" dir="auto">
                        {e.title}
                      </span>
                      <span className="text-xs text-muted">
                        {pluralize(e.sprintCount, "sprint")} · {pluralize(e.taskCount, "task")}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5 ps-7">
                      {epicProps
                        .filter((p) => p.type !== "date")
                        .flatMap((p) =>
                          (e.values[p.id] ?? []).map((id) => {
                            const o = p.options.find((x) => x.id === id);
                            return o ? (
                              <Tag key={id} color={o.color}>
                                {o.label}
                              </Tag>
                            ) : null;
                          }),
                        )}
                      {dates.map((p) => (
                        <span key={p.id} className="text-xs text-muted">
                          {p.name}: {formatDay(e.values[p.id][0], true)}
                        </span>
                      ))}
                    </div>
                    <ProgressBar done={e.doneCount} total={e.taskCount} className="mt-2 ps-7" />
                  </Link>
                );
              })}
            </div>
          </section>
          <aside>
            <h2 className="mb-3 text-lg font-semibold">Recent activity</h2>
            <RecentActivity />
          </aside>
        </div>
      </div>
    </>
  );
}
